<template>
  <div class="dashboard">
    <!-- USPS Form Alert -->
    <div class="alert-section" v-if="showUSPSAlert">
      <div class="alert">
        <i class="fas fa-info-circle"></i>
        <div class="alert-content">
          <h4>{{ $t('customerDashboard.uspsAlert.title') }}</h4>
          <p>{{ $t('customerDashboard.uspsAlert.message') }}</p>
          <button class="complete-form-btn" @click="completeForm">{{ $t('customerDashboard.uspsAlert.button') }}</button>
        </div>
      </div>
    </div>

    <!-- Implementation Alert -->
    <div class="alert-section" v-if="showImplementationAlert">
      <div class="alert">
        <i class="fas fa-info-circle"></i>
        <div class="alert-content">
          <h4>{{ $t('customerDashboard.comingSoon.title') }}</h4>
          <p>{{ $t('customerDashboard.comingSoon.message') }}</p>
          <button class="complete-form-btn" @click="showImplementationAlert = false">{{ $t('customerDashboard.comingSoon.button') }}</button>
        </div>
      </div>
    </div>

    <!-- Welcome Section -->
    <h1 class="welcome-title">{{ $t('customerDashboard.welcome', { businessName: dashboardData.businessName || 'Tartoro' }) }}</h1>

    <!-- Business Formation Progress -->
    <div class="progress-section">
      <h2>{{ $t('customerDashboard.progress.title') }}</h2>
      <div class="progress-timeline">
        <div class="progress-step completed">
          <div class="step-number">1</div>
          <div class="step-content">
            <h3>{{ $t('customerDashboard.progress.steps.application.title') }}</h3>
            <p>{{ $t('customerDashboard.progress.steps.application.date', { date: formatDate(dashboardData.formationDate) }) }}</p>
          </div>
        </div>
        <div class="progress-step completed">
          <div class="step-number">2</div>
          <div class="step-content">
            <h3>{{ $t('customerDashboard.progress.steps.ra.title') }}</h3>
            <p>{{ dashboardData.registeredAgent ? $t('customerDashboard.progress.steps.ra.status.assigned') : $t('customerDashboard.progress.steps.ra.status.pending') }}</p>
          </div>
        </div>
        <div class="progress-step" :class="{ 'current': dashboardData.status === 'pending' }">
          <div class="step-number">3</div>
          <div class="step-content">
            <h3>{{ $t('customerDashboard.progress.steps.llc.title') }}</h3>
            <p>{{ dashboardData.status === 'pending' ? $t('customerDashboard.progress.steps.llc.status.inProgress') : $t('customerDashboard.progress.steps.llc.status.pending') }}</p>
          </div>
        </div>
        <div class="progress-step" :class="{ 'current': dashboardData.status === 'active' }">
          <div class="step-number">4</div>
          <div class="step-content">
            <h3>{{ $t('customerDashboard.progress.steps.ein.title') }}</h3>
            <p>{{ dashboardData.ein === 'Pending' ? $t('customerDashboard.progress.steps.ein.status.pending') : $t('customerDashboard.progress.steps.ein.status.completed') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="quick-actions">
      <h2>{{ $t('customerDashboard.quickActions.title') }}</h2>
      <div class="action-cards">
        <div class="action-card">
          <div class="card-icon">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="20" fill="#E8F5E9"/>
              <rect x="12" y="10" width="16" height="20" rx="2" stroke="#2E7D32" stroke-width="2" fill="white"/>
              <path d="M20 18v8M20 26l-3-3m3 3l3-3" stroke="#2E7D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3>{{ $t('customerDashboard.quickActions.articles.title') }}</h3>
          <p>{{ $t('customerDashboard.quickActions.articles.description') }}</p>
          <button class="action-btn" @click="showComingSoon">{{ $t('customerDashboard.quickActions.articles.button') }}</button>
        </div>
        <div class="action-card">
          <div class="card-icon">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="20" fill="#E8F5E9"/>
              <rect x="12" y="10" width="16" height="20" rx="2" stroke="#2E7D32" stroke-width="2" fill="white"/>
              <path d="M20 18v8M20 26l-3-3m3 3l3-3" stroke="#2E7D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3>{{ $t('customerDashboard.quickActions.receipt.title') }}</h3>
          <p>{{ $t('customerDashboard.quickActions.receipt.description') }}</p>
          <button class="action-btn" @click="showComingSoon">{{ $t('customerDashboard.quickActions.receipt.button') }}</button>
        </div>
        <div class="action-card">
          <div class="card-icon">
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="20" fill="#E8F5E9"/>
              <rect x="12" y="10" width="16" height="20" rx="2" stroke="#2E7D32" stroke-width="2" fill="white"/>
              <path d="M16 22l3 3 5-5" stroke="#2E7D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3>{{ $t('customerDashboard.quickActions.fein.title') }}</h3>
          <p>{{ $t('customerDashboard.quickActions.fein.description') }}</p>
          <button class="action-btn" @click="showComingSoon">{{ $t('customerDashboard.quickActions.fein.button') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@/styles/CustomerDashboard.css'
import businessService from '@/services/businessService'

export default {
  name: 'CustomerDashboard',
  props: {
    selectedBusiness: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      showUSPSAlert: true,
      showImplementationAlert: false,
      dashboardData: {
        businessName: '',
        status: '',
        businessCategory: '',
        selectedPackage: '',
        id: '',
        ein: 'Pending',
        formationDate: '',
        nextComplianceDate: '',
        documentsCount: 0,
        mailCount: 0,
        registeredAgent: null
      }
    }
  },
  computed: {
    currentBusinessId() {
      // First try to get from route params
      if (this.$route.params.businessId) {
        return this.$route.params.businessId;
      }
      // Then try from selectedBusiness prop
      if (this.selectedBusiness && this.selectedBusiness.id) {
        return this.selectedBusiness.id;
      }
      // Finally try from localStorage
      const businesses = JSON.parse(localStorage.getItem('userBusinesses') || '[]');
      return businesses.length > 0 ? businesses[0].id : null;
    },
    currentLocale() {
      return this.$i18n.locale;
    }
  },
  watch: {
    currentLocale: {
      immediate: true,
      handler() {
        // Use nextTick to ensure component is mounted
        this.$nextTick(() => {
          if (this.$el) {
            this.$forceUpdate();
          }
        });
      }
    },
    selectedBusiness: {
      immediate: true,
      handler(newBusiness) {
        if (newBusiness) {
          this.fetchBusinessData();
        }
      }
    },
    '$route.params.businessId': {
      immediate: true,
      handler(newBusinessId) {
        if (newBusinessId) {
          this.fetchBusinessData();
        }
      }
    }
  },
  created() {
    this.fetchBusinessData();
  },
  mounted() {
    // Ensure component is properly mounted before any updates
    this.$nextTick(() => {
      if (this.$el) {
        this.$forceUpdate();
      }
    });
  },
  methods: {
    async fetchBusinessData() {
      try {
        const businessId = this.currentBusinessId;
        if (businessId) {
          const response = await businessService.getBusiness(businessId);
          if (response && response.data) {
            this.dashboardData = {
              businessName: response.data.legalBusinessName || '',
              status: response.data.status || '',
              businessCategory: response.data.businessCategory || '',
              selectedPackage: response.data.selectedPackage || '',
              id: response.data.id || '',
              ein: response.data.ein || 'Pending',
              formationDate: response.data.declarationDate || '',
              nextComplianceDate: response.data.nextComplianceDate || '',
              documentsCount: response.data.documentsCount || 0,
              mailCount: response.data.mailCount || 0,
              registeredAgent: response.data.agentId || null
            };
          }
        }
      } catch (error) {
        console.error('Error fetching business data:', error);
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'Not specified';
      const date = new Date(dateString);
      return date.toLocaleDateString(this.currentLocale === 'en' ? 'en-US' : 'es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    completeForm() {
      // Handle form completion
      this.showUSPSAlert = false;
    },
    showComingSoon() {
      this.showImplementationAlert = true;
      setTimeout(() => {
        this.showImplementationAlert = false;
      }, 5000);
    }
  }
}
</script>

<style scoped>
.dashboard {
  margin: 1rem 1.5rem;
  padding: 0 1rem;
}
</style> 